<template>
    <div>
        <h3 class="mb-3">Asset Register</h3>
        <vs-tabs v-model="activeTab">
            <vs-tab label="List" icon-pack="feather" icon="icon-list">
                <div class="mt-6">
                    <template v-if="viewAsset">
                        <vs-row>
                            <vs-col vs-xs="12" vs-sm="10" vs-lg="10" class="px-0 pr-2 mb-base">
                                <vs-input
                                    class="w-full"
                                    placeholder="Type to Search"
                                    v-model="filters.search"
                                />
                            </vs-col>
                            <vs-col vs-xs="12" vs-sm="2" vs-lg="2" class="mb-base p-0 text-right">
                                <vx-tooltip text="Create New" color="primary" class="inline-block">
                                    <vs-button color="primary" type="filled" @click="activeTab = 1; resetForm();" :disabled="loading">
                                        <font-awesome-icon :icon="addIcon" />
                                    </vs-button>
                                </vx-tooltip>
                                <vx-tooltip text="Refresh Data" color="primary" class="ml-2 inline-block">
                                    <vs-button color="primary" type="filled" @click="init()" :disabled="loading">
                                        <font-awesome-icon :icon="redoIcon" />
                                    </vs-button>
                                </vx-tooltip>
                            </vs-col>
                        </vs-row>
                        <div class="vx-col w-full mb-base">
                            <vx-card>
                                <vs-table :data="results" slot="no-body" :hoverFlat="true" stripe>
                                    <vs-tr>
                                        <vs-th rowspan="2">Asset ID</vs-th>
                                        <vs-th rowspan="2">Category</vs-th>
                                        <vs-th colspan="2">Chainage Value</vs-th>
                                        <vs-th rowspan="2">Reporter Name</vs-th>
                                        <vs-th rowspan="2">Date Created</vs-th>
                                        <vs-th rowspan="2">Action</vs-th>
                                    </vs-tr>
                                    <vs-tr>
                                        <vs-th>From</vs-th>
                                        <vs-th>To</vs-th>
                                    </vs-tr>
                                    <template>
                                        <template v-for="(item, index) in results">
                                            <vs-tr :key="index">
                                                <vs-td>
                                                    {{ item.id }}
                                                </vs-td>
                                                <vs-td>
                                                    {{ item.category ? item.category.name : '-' }}
                                                </vs-td>
                                                <vs-td>
                                                    {{ item.chainage_from }}
                                                </vs-td>
                                                <vs-td>
                                                    {{ item.chainage_to }}
                                                </vs-td>
                                                <vs-td>
                                                    {{ item.reported_by.first_name }} {{ item.reported_by.last_name }}
                                                </vs-td>
                                                <vs-td>
                                                    <FormatText :text="item.created_on" format="datetime" />
                                                </vs-td>
                                                <vs-td>
                                                    <vs-button type="filled" size="small" color="primary" @click="editItem(item)">View/Edit</vs-button>
                                                </vs-td>
                                            </vs-tr>
                                        </template>
                                    </template>
                                </vs-table>
                            </vx-card>
                        </div>
                        <template v-if="!disable_pagination">
                            <vs-row v-if="total_results" class="mt-3 text-center">
                                <vs-pagination :total="total_pages" v-model="current_page" goto class="mx-auto"></vs-pagination>
                            </vs-row>
                        </template>
                    </template>
                    <template v-else>
                        <PermissionDenied />
                    </template>
                </div>
            </vs-tab>
            <vs-tab :label="editingId ? 'View/Edit Asset' : 'Add New Asset'" icon-pack="feather" :icon="editingId ? 'icon-edit' : 'icon-plus'">
                <div class="mt-6">
                    <template v-if="hasFormPermission"> 
                        <vx-card noShadow noRound>
                            <div class="mb-4">
                                <h3 class="mb-2">{{ editingId ? 'View/Edit Asset' : 'Create Asset' }}</h3>
                                <p class="text-muted mb-4" v-if="!editingId">Fill in the details below to create a new asset entry.</p>
                            </div>
                            <vs-row>
                                <vs-col vs-xs="12" vs-sm="4" vs-lg="4" class="mb-3 pr-2">
                                    <vs-input
                                        :value="asset_register.id ? `Asset #${asset_register.id}` : 'Auto Generated'"
                                        label="Asset ID"
                                        readonly
                                        class="w-full"
                                    />
                                </vs-col>
                                <vs-col vs-xs="12" vs-sm="4" vs-lg="4" class="mb-3 pr-2">
                                    <SelectForeign
                                        :model="asset_register.reported_by"
                                        label="Reported By"
                                        placeholder="Auto Filled"
                                        :multiple="false"
                                        :load_initial="false"
                                        :label_keys="['first_name', 'last_name']"
                                        :show_id_in_label="true"
                                        :endpoint="`${$this.$store.state.apiendpoints.organisation_users}?is_active=true`"
                                        container_class="w-full"
                                        input_class=""
                                        :use_id_as_value="false"
                                        :disabled="true"
                                    />
                                </vs-col>
                                <vs-col vs-xs="12" vs-sm="4" vs-lg="4" class="mb-3">
                                    <label class="vs-input--label block mb-1">Date Created</label>
                                    <div class="mt-2">
                                        <FormatText :text="asset_register.created_on" format="datetime" />
                                    </div>
                                </vs-col>
                            </vs-row>

                            <vs-row>
                                <vs-col vs-xs="12" vs-sm="4" vs-lg="4" class="mb-3 pr-2">
                                    <SelectForeign
                                        :model.sync="asset_register.category"
                                        label="Category"
                                        placeholder="Type to Search.."
                                        :multiple="false"
                                        :load_initial="true"
                                        :label_keys="['name']"
                                        :show_id_in_label="false"
                                        :endpoint="`${$this.$store.state.apiendpoints.data_categories}?issue_type_category=asset_register&active=true`"
                                        container_class="w-full"
                                        input_class=""
                                        :use_id_as_value="false"
                                        :disabled="!editPermission"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.category">
                                        {{ api_errors.category.join(', ') }}
                                    </small>
                                </vs-col>
                                <vs-col vs-xs="12" vs-sm="8" vs-lg="8" class="mb-3">
                                    <label class="vs-input--label block">Chainage</label>
                                    <vs-row class="mt-1">
                                        <vs-col vs-xs="6" vs-sm="6" vs-lg="6" class="pl-0 pr-2">
                                            <div class="flex" style="align-items: center">
                                                <label class="vs-input--label block mr-3">From</label>
                                                <vs-input
                                                    v-model="asset_register.chainage_from"
                                                    class="w-full"
                                                    :disabled="!editPermission"
                                                />
                                            </div>
                                            <small class="block text-danger mt-1" v-if="api_errors.chainage_from">
                                                {{ api_errors.chainage_from.join(', ') }}
                                            </small>
                                        </vs-col>
                                        <vs-col vs-xs="6" vs-sm="6" vs-lg="6" class="pl-2 pr-0">
                                            <div class="flex" style="align-items: center">
                                                <label class="vs-input--label block mr-3">To</label>
                                                <vs-input
                                                    v-model="asset_register.chainage_to"
                                                    class="w-full"
                                                    :disabled="!editPermission"
                                                />
                                            </div>
                                            <small class="block text-danger mt-1" v-if="api_errors.chainage_to">
                                                {{ api_errors.chainage_to.join(', ') }}
                                            </small>
                                        </vs-col>
                                    </vs-row>
                                </vs-col>
                            </vs-row>

                            <vs-row>
                                <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="mb-3">
                                    <label class="vs-input--label block mb-1">Description</label>
                                    <vs-textarea v-model="asset_register.observation" :disabled="!editPermission" />
                                    <small class="block text-danger mt-1" v-if="api_errors.observation">
                                        {{ api_errors.observation.join(', ') }}
                                    </small>
                                </vs-col>
                            </vs-row>

                            <vs-row>
                                <vs-col vs-xs="12" vs-sm="4" vs-lg="4" class="px-0 pr-2">
                                    <label class="vs-input--label block mb-1">Images</label>
                                    <FileSelection
                                        :images.sync="asset_register.images"
                                        :read_only="!editPermission"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.images">
                                        {{ api_errors.images.join(', ') }}
                                    </small>
                                </vs-col>
                                <vs-col vs-xs="12" vs-sm="8" vs-lg="8" class="px-0">
                                    <LocationChooser :disabled="!editPermission" :latitude.sync="asset_register.location_latitude" :longitude.sync="asset_register.location_longitude" :api_errors="{latitude: api_errors.location_latitude, longitude: api_errors.location_longitude}" />
                                </vs-col>
                            </vs-row>

                            <vs-row class="mt-base">
                                <vs-col class="text-right">
                                    <vs-button @click="activeTab = 0; resetForm();" color="warning" type="flat" class="mr-2">Cancel</vs-button>
                                    <vs-button @click="save" :disabled="submit_disabled" v-if="editPermission" color="primary" type="filled">
                                        {{ editingId ? 'Update' : 'Save' }}
                                    </vs-button>
                                </vs-col>
                            </vs-row>
                        </vx-card>
                    </template>
                    <template v-else>
                        <PermissionDenied />
                    </template>
                </div>
            </vs-tab>
        </vs-tabs>
    </div>
</template>


<script>
import { faPlus, faRedo } from '@fortawesome/free-solid-svg-icons';
import {Request} from "../../utils/requests"
import FileSelection from "../Issue/Components/FileSelection.vue"
import LocationChooser from '../Issue/Components/LocationChooser.vue'

export default {
    components: {FileSelection, LocationChooser},
	props: {
        embedded_mode: {
            required: false,
            default: false
        },
        embedded_filters: {
            required: false,
            default: () => {}
        },
        disable_pagination: {
            required: false,
            default: false
        },
        global_mode: {
            required: false,
            type: Boolean,
            default: false
        }
    },
    data() {
        let offset = parseInt(this.$route.query.offset || 0);
        let limit = parseInt(this.$route.query.limit || 20);

        let current_page = Math.ceil((offset + limit) / limit);
        return  {
            results: [],

			next_url: null,
            loading: false,
            total_results: null,
            current_page: current_page,
            filters: {
                search: this.$route.query.search || null,
                ordering: this.$route.query.ordering || [],
                offset: offset,
                limit: limit
            },

            activeTab: 0,
            editingId: null,
            asset_register: {
                id: null,
                reported_by: this.$store.state.organisation.organisation_user,
                created_on: new Date().toISOString(),
                category: null,
                chainage_from: null,
                chainage_to: null,
                observation: null,
                location_latitude: null,
                location_longitude: null,
                images: []
            },
            submit_disabled: false,
            api_errors: {}
        }
    },
    mounted() {
        this.init()
    },
	computed: {
        total_pages() {
            if(this.total_results == 0 || this.filters.limit == 0)  return 1
            return Math.ceil(this.total_results/this.filters.limit)
        },
        redoIcon: () => faRedo,
        addIcon: () => faPlus,
        viewAsset() { 
            return this.hasAccess('read', 'asset_register', ['all', 'own'])
        },
        createAsset() { 
            return this.hasAccess('create', 'asset_register', 'all') 
        },
        editAsset() { 
            let reported_by_id = (this.asset_register.reported_by && this.asset_register.reported_by.id) ? this.asset_register.reported_by.id : null;
            return this.hasAccess('edit', 'asset_register', ['all', 'own'], reported_by_id ? [reported_by_id] : []) 
        },
        hasFormPermission() {
            return (!this.editingId && this.createAsset) || (this.editingId && this.editAsset) || (this.editingId && this.viewAsset)
        },
        editPermission() {
            return (!this.editingId && this.createAsset) || (this.editingId && this.editAsset)
        },
        $this() { return this; }
    },
    watch: {
        filters: {
            deep: true,
            handler(newV) {
                if (this.current_page == null) return;

                if (this.timer) clearTimeout(this.timer);

                this.timer = setTimeout(() => {
                    this.$router.push({ name: this.$route.name, query: {...newV, ...this.embedded_query_params} });
                }, 800);
            }
        },
        "$route.query"(newV) {
            this.load_data(newV);
        },
        current_page(newV, oldV) {
            if (newV == oldV) return;
            this.filters.offset = (newV - 1) * this.filters.limit;
        }
    },
    methods: {
        init(){
            this.load_data(this.filters)
        },
        calculate_page() {
            if (this.filters.offset == 0) this.current_page = 1;
            else
                this.current_page = Math.ceil(this.filters.offset / this.filters.limit);
        },
        load_data(filters=null) {
            this.loading = true;
            this.$vs.loading();

            let url = this.$store.state.apiendpoints.data_asset_registers

            let payload = filters

            if (!payload) payload = {};

            if (this.embedded_mode && this.embedded_filters) {
                payload = { ...payload, ...this.embedded_filters };
            }

            if (this.global_mode) {
                payload['global'] = true
            }

            this.api_errors = {}

            Request(this, 'GET', url, payload).then( 
                (res) => {
                    this.results = res.results
                    this.total_results = res.count
                    this.next_url = res.next;
                    this.loading = false;
                    this.$vs.loading.close();
                }, 
                (err) => {
                    this.loading = false;
                    this.$vs.loading.close();
                    if (err.error) {
                        this.api_errors = err.error

                        this.$vs.notify({
                            time: 2500,
                            title: 'Error',
                            text: "Please fix the errors and try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    } else {
                        this.$vs.notify({
                            time: 2500,
                            title: 'Error',
                            text: "Unable to load asset registers. Please try again later.",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    }
                }
            )
        },
        resetForm() {
            this.editingId = null
            this.asset_register = {
                id: null,
                reported_by: this.$store.state.organisation.organisation_user,
                created_on: new Date().toISOString(),
                category: null,
                chainage_from: null,
                chainage_to: null,
                observation: null,
                location_latitude: null,
                location_longitude: null,
                images: []
            }
            this.api_errors = {}
        },
        editItem(item) {
            this.editingId = item.id
            this.activeTab = 1
            this.load_item_data(item.id)
        },
        load_item_data(asset_id) {
            let request_method = 'GET', url = this.$store.state.apiendpoints.data_asset_register + asset_id + "/"
            this.$vs.loading()
            Request(this, request_method, url).then(
                res => {
                    this.$vs.loading.close()
                    this.asset_register = res
                },
                err => {
                    this.$vs.loading.close()
                    this.$vs.notify({
                        time: 3000,
                        title: 'Error',
                        text: "An unknown error occurred! Please try again!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                }
            )
        },
        save() {
            let request_method = 'POST', url = this.$store.state.apiendpoints.data_asset_register
            if (this.editingId) {
                request_method = 'PUT'
                url += this.editingId + '/'
            }

            let payload = {
                ...this.asset_register
            }

            if (this.asset_register.images.length == 0) {
                this.api_errors = {
                    "images": [
                        "Please select atleast 1 image",
                    ]
                }
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Please fix the errors and try again!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                })
                return
            }

            this.submit_disabled = true
            this.$vs.loading()

            Request(this, request_method, url, null, payload).then(
                async res => {
                    try {
                        await this.upload_images(res.id)
                        this.asset_register = {...res, images: this.asset_register.images}
                        this.$vs.loading.close()
                        this.submit_disabled = false

                        this.$vs.notify({
                            time: 3000,
                            title: 'Saved',
                            text: "Saved Successfully!",
                            iconPack: 'feather',
                            icon: 'icon-check-circle',
                            color: 'success'
                        })
                        this.resetForm()
                        this.activeTab = 0
                        this.init()
                    } catch (e) {
                        this.$vs.loading.close()
                        this.submit_disabled = false
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "An error occurred while uploading images. Please try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    }
                },
                err => {
                    this.$vs.loading.close()
                    this.submit_disabled = false
                    if (err.error) {
                        this.api_errors = err.error
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "Please fix the errors and try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    } else {
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "An unknown error occurred! Please try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    }
                }
            )
        },
        async upload_images(asset_id) {
            for (let i=0; i < this.asset_register.images.length; i++) {
                this.$set(this.asset_register.images[i], 'status', 'u')
                try {
                    let data = await this.upload_single_image(asset_id, this.asset_register.images[i])
                    this.$set(this.asset_register.images[i], 'status', 'c')
                    if (data != null)
                        this.$set(this.asset_register.images, i, data)
                } catch (e) {
                    this.$set(this.asset_register.images[i], 'status', 'f')
                    throw e
                }
            }
        },
        async upload_single_image(asset_id, image_obj) {
            return new Promise( (resolve, reject) => {
                if (typeof(image_obj) == 'string') resolve(null)
    
                let payload = new FormData()
                payload.append('image', image_obj.image)

                Request(this, 'POST', this.sprintf(this.$this.$store.state.apiendpoints.data_asset_register_image, asset_id), null, payload).then(
                    res => resolve(res),
                    err => reject(err)
                )
            })
        },
    }
}

</script>

<style lang="scss" scoped>
.text-muted {
    color: #6c757d;
    font-size: 0.9rem;
}
.align-center {
    align-items: center;
}
.mr-4 {
    margin-right: 1rem;
}
</style>
